import React, { useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEYS = ["title", "subtitle", "whatsapp", "phone", "email", "address", "footer_notes", "accent_color", "public_enabled", "public_price_type", "show_prices_public", "public_freight_note"];

const Field = ({ label, children }) => (<div><label className="text-xs font-bold">{label}</label>{children}</div>);

export default function CatalogConfigDialog({ companyId, config, onClose, onSaved }) {
  const [d, setD] = useState(() => Object.fromEntries(KEYS.map((k) => [k, config[k] ?? ""])));
  const set = (patch) => setD((x) => ({ ...x, ...patch }));

  const save = async () => {
    const body = Object.fromEntries(KEYS.map((k) => [k, d[k] === "" ? null : d[k]]));
    body.title = d.title || "Catálogo de Produtos";
    body.accent_color = d.accent_color || "#047857";
    body.public_price_type = d.public_price_type || "varejo";
    try {
      const { data } = await axios.put(`${API}/companies/${companyId}/factory/catalog/config`, body);
      toast.success("Configuração do catálogo salva");
      onSaved(data);
      onClose();
    } catch (e) { toast.error("Erro: " + (e.response?.data?.detail || "")); }
  };

  const regenerate = async () => {
    if (!window.confirm("Gerar um novo link público? O link antigo deixará de funcionar.")) return;
    const { data } = await axios.post(`${API}/companies/${companyId}/factory/catalog/regenerate-link`);
    toast.success("Novo link gerado");
    onSaved(data);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="catalog-config-dialog">
        <DialogHeader><DialogTitle>Configurar Catálogo</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Título"><Input value={d.title} onChange={(e) => set({ title: e.target.value })} data-testid="catalog-cfg-title" /></Field>
          <Field label="Cor principal"><Input type="color" value={d.accent_color || "#047857"} onChange={(e) => set({ accent_color: e.target.value })} className="h-9 p-1" data-testid="catalog-cfg-color" /></Field>
          <div className="col-span-2"><Field label="Subtítulo / chamada"><Input value={d.subtitle} onChange={(e) => set({ subtitle: e.target.value })} placeholder="Argamassas industrializadas de alta qualidade" data-testid="catalog-cfg-subtitle" /></Field></div>
          <Field label="WhatsApp"><Input value={d.whatsapp} onChange={(e) => set({ whatsapp: e.target.value })} placeholder="(82) 99999-9999" data-testid="catalog-cfg-whatsapp" /></Field>
          <Field label="Telefone"><Input value={d.phone} onChange={(e) => set({ phone: e.target.value })} data-testid="catalog-cfg-phone" /></Field>
          <Field label="E-mail"><Input value={d.email} onChange={(e) => set({ email: e.target.value })} data-testid="catalog-cfg-email" /></Field>
          <Field label="Endereço"><Input value={d.address} onChange={(e) => set({ address: e.target.value })} data-testid="catalog-cfg-address" /></Field>
          <div className="col-span-2"><Field label="Observações do rodapé (condições, validade dos preços...)"><Textarea rows={2} value={d.footer_notes} onChange={(e) => set({ footer_notes: e.target.value })} data-testid="catalog-cfg-footer" /></Field></div>
        </div>
        <div className="border rounded p-3 mt-2 space-y-2 bg-sky-50">
          <label className="flex items-center gap-2 text-sm font-bold cursor-pointer">
            <input type="checkbox" checked={!!d.public_enabled} onChange={(e) => set({ public_enabled: e.target.checked })} data-testid="catalog-cfg-public" />
            Ativar link público (qualquer pessoa com o link pode ver)
          </label>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Tabela de preço do link">
              <Select value={d.public_price_type || "varejo"} onValueChange={(v) => set({ public_price_type: v })}>
                <SelectTrigger data-testid="catalog-cfg-public-price"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="varejo">Varejo</SelectItem>
                  <SelectItem value="atacado">Atacado</SelectItem>
                  <SelectItem value="especial">Especial</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Texto do frete no link"><Input value={d.public_freight_note} onChange={(e) => set({ public_freight_note: e.target.value })} placeholder="Frete a combinar" data-testid="catalog-cfg-freight-note" /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={d.show_prices_public !== false} onChange={(e) => set({ show_prices_public: e.target.checked })} data-testid="catalog-cfg-show-prices" />
            Mostrar preços no link público
          </label>
          <Button size="sm" variant="outline" onClick={regenerate} data-testid="catalog-cfg-regenerate"><RefreshCw className="h-3 w-3 mr-1" />Gerar novo link</Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} data-testid="catalog-cfg-save">Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
